import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { useRuntimeConfig } from './runtime-config.js';

/**
 * Session handling.
 *
 * The browser holds only the Supabase anon key and the user's own access token.
 * Every privileged call goes to our worker, which verifies the token server-side
 * — the dashboard never talks to a provider directly.
 */

interface SessionState {
  readonly client: SupabaseClient | null;
  readonly session: Session | null;
  readonly loading: boolean;
  readonly configured: boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}

/** The access token for API calls, or null when signed out. */
export function useAccessToken(): string | null {
  return useSession().session?.access_token ?? null;
}

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const config = useRuntimeConfig();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const client = useMemo(() => {
    if (!config.supabaseUrl || !config.supabaseAnonKey) return null;
    return createClient(config.supabaseUrl, config.supabaseAnonKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    });
  }, [config.supabaseUrl, config.supabaseAnonKey]);

  useEffect(() => {
    if (!client) {
      setLoading(false);
      return;
    }

    let active = true;
    void client.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = client.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [client]);

  const value = useMemo<SessionState>(
    () => ({ client, session, loading, configured: client !== null }),
    [client, session, loading],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
